import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Group,
  LoadingOverlay,
  Paper,
  Popover,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { buildStoreUrl } from '../../../utils/storeUrl.ts';
import {
  IconBolt,
  IconBrowser,
  IconChargingPile,
  IconClock,
  IconCreditCard,
  IconDatabaseOff,
  IconEdit,
  IconPhoto,
  IconPlus,
  IconTrash,
  IconZoomMoney,
} from '@tabler/icons-react';
import {
  getServiceStatusColor,
  getServiceStatusLabel,
} from '../../../const/service_status_mapping.ts';
import { dayMap } from '../../../utils/DateUtil.ts';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import { getConnectorTypeLabel } from '../../../const/charging_connector_type_mapping.ts';
import React from 'react';
import useChargingService from '../../../hooks/charging-services/useChargingService.ts';
import useBrand from '../../../hooks/brands/useBrand.ts';
import useEVSEs from '../../../hooks/charging-services/useEVSEs.ts';
import { modals } from '@mantine/modals';
import { ServiceType } from '../../../models/ServiceType.ts';
import { useQueries } from '@tanstack/react-query';
import { getTag } from '../../../api/tag.ts';
import { getPaymentMethod } from '../../../api/payment-method.ts';
import { useTheme } from '../../../context/ThemeProvider.tsx';

const phoneUtil = PhoneNumberUtil.getInstance();

interface ChargingServiceInformationCardProps {
  serviceId: string;
  setIsServiceImageCarouselOpen: (isServiceImageCarouselOpen: boolean) => void;
  setSelectedServiceIdForCarousel: (
    selectedServiceIdForCarousel: string | null,
  ) => void;
  setSelectedServiceTypeForCarousel: (
    selectedServiceTypeForCarousel: ServiceType | null,
  ) => void;
  handleEditChargingServiceMetadataClick: (serviceId: string) => void;
  handleAddEVSEAndConnectorsClick: (serviceId: string) => void;
  handleUpdateEVSEAndConnectorsClick: (
    serviceId: string,
    evseId: string,
  ) => void;
  handleDeleteEVSEAndConnectorsClick: (
    serviceId: string,
    evseId: string,
  ) => void;
  handleAddChargingConnectorClick: (serviceId: string, evseId: string) => void;
  handleDeleteChargingServiceClick: (serviceId: string) => void;
  handleUpdateChargingServiceHeaderInfoClick: (serviceId: string) => void;
}

const ChargingServiceInformationCard: React.FC<
  ChargingServiceInformationCardProps
> = ({
  serviceId,
  setIsServiceImageCarouselOpen,
  setSelectedServiceIdForCarousel,
  setSelectedServiceTypeForCarousel,
  handleEditChargingServiceMetadataClick,
  handleAddEVSEAndConnectorsClick,
  handleUpdateEVSEAndConnectorsClick,
  handleDeleteEVSEAndConnectorsClick,
  handleAddChargingConnectorClick,
  handleDeleteChargingServiceClick,
  handleUpdateChargingServiceHeaderInfoClick,
}) => {
  const { data: service, isLoading: isChargingServiceLoading } =
    useChargingService(serviceId);
  const { data: brand, isLoading: isBrandLoading } = useBrand(service?.brandId);
  const { data: evses, isLoading: isEVSEsLoading } = useEVSEs(serviceId);

  const tagQueries = useQueries({
    queries: (service?.tags ?? []).map((tagId) => ({
      queryKey: ['tags', tagId],
      queryFn: () => getTag(tagId),
      enabled: !!tagId,
    })),
  });
  const tagNames = tagQueries.map((q) => q.data?.name).filter(Boolean);
  const isTagsLoading = tagQueries.some((q) => q.isLoading);

  const paymentMethodQueries = useQueries({
    queries: (service?.paymentMethods ?? []).map((paymentMethodId) => ({
      queryKey: ['paymentMethods', paymentMethodId],
      queryFn: () => getPaymentMethod(paymentMethodId),
      enabled: !!paymentMethodId,
    })),
  });
  const paymentMethodNames = paymentMethodQueries
    .map((q) => q.data?.name)
    .filter(Boolean);
  const isPaymentMethodsLoading = paymentMethodQueries.some((q) => q.isLoading);

  const { colorScheme } = useTheme();

  if (
    isBrandLoading ||
    isChargingServiceLoading ||
    isEVSEsLoading ||
    isTagsLoading ||
    isPaymentMethodsLoading
  )
    return <LoadingOverlay />;

  return (
    <>
      <Paper
        key={serviceId}
        shadow="xs"
        style={{ height: 588, borderRadius: 16, marginBottom: 20 }}
      >
        <Group
          style={{
            justifyContent: 'space-between',
            paddingInline: 30,
            paddingTop: 38,
            paddingBottom: 19,
          }}
        >
          <Group gap={18}>
            <ThemeIcon
              style={{
                width: 62,
                height: 62,
                backgroundColor: '#20C997',
                borderRadius: '50%',
              }}
            >
              <IconBolt style={{ width: 40, height: 40 }} />
            </ThemeIcon>

            <Stack gap={10}>
              <Group>
                <Text
                  style={{
                    lineHeight: '32px',
                    fontSize: 24,
                    fontWeight: 700,
                  }}
                >
                  {service.name}
                </Text>
                <ActionIcon size={'sm'} variant={'light'}>
                  <IconEdit
                    style={{ width: 20, height: 20 }}
                    onClick={() => {
                      handleUpdateChargingServiceHeaderInfoClick(service.id);
                    }}
                  />
                </ActionIcon>
              </Group>

              <Group gap={10}>
                {/* 服務狀態 */}
                <Badge color={getServiceStatusColor(service.serviceStatus)}>
                  {getServiceStatusLabel(service.serviceStatus)}
                </Badge>
                {/* 標籤 */}
                {tagNames.map((tag) => (
                  <Badge variant={'outline'}>{tag}</Badge>
                ))}
              </Group>
            </Stack>
          </Group>

          <ActionIcon size={'lg'} color={'red'} variant={'light'}>
            <IconTrash
              style={{ width: 20, height: 20 }}
              onClick={() =>
                modals.openConfirmModal({
                  title: (
                    <Text
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                      }}
                    >
                      刪除充電服務
                    </Text>
                  ),
                  children: (
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                      }}
                    >
                      是否確認刪除充電服務？
                    </Text>
                  ),
                  centered: true,
                  labels: {
                    confirm: '確認',
                    cancel: '取消',
                  },
                  confirmProps: { color: 'blue' },

                  onConfirm: async () => {
                    handleDeleteChargingServiceClick(serviceId);
                  },
                })
              }
            />
          </ActionIcon>
        </Group>

        <div
          style={{
            height: 1,
            backgroundColor: colorScheme === 'light' ? '#dee2e6' : '#737373ff',
            marginInline: 30,
          }}
        />

        <div
          style={{
            position: 'relative',
            display: 'flex',
            height: 410,
            paddingInline: 30,
            marginTop: 20,
          }}
        >
          {/* 左半邊 */}
          <div style={{ flex: 1, height: 410, marginRight: 40, minWidth: 0 }}>
            <Group
              justify={'space-between'}
              style={{ height: 40, marginBottom: 22 }}
            >
              <Text style={{ fontSize: 20, fontWeight: 400 }}>服務資訊</Text>
              <Button
                variant={'light'}
                leftSection={<IconEdit />}
                onClick={() =>
                  handleEditChargingServiceMetadataClick(serviceId)
                }
              >
                編輯
              </Button>
            </Group>

            <Group gap={16} style={{ marginBottom: 20 }}>
              <Button
                onClick={() => {
                  setSelectedServiceIdForCarousel(serviceId);
                  setSelectedServiceTypeForCarousel('CHARGING');
                  setIsServiceImageCarouselOpen(true);
                }}
                disabled={service.imageIds.length === 0}
                variant={'outline'}
                leftSection={<IconPhoto style={{ width: 16, height: 16 }} />}
                radius={'xl'}
                style={{
                  width: 112,
                  height: 32,
                  fontSize: 14,
                  fontWeight: 400,
                }}
                styles={{ section: { marginRight: 8 } }}
              >
                服務圖片
              </Button>
              <Button
                component={'a'}
                href={service.website}
                disabled={service.website === ''}
                target={'_blank'}
                variant={'outline'}
                leftSection={<IconBrowser style={{ width: 16, height: 16 }} />}
                radius={'xl'}
                style={{
                  width: 112,
                  height: 32,
                  fontSize: 14,
                  fontWeight: 400,
                }}
                styles={{ section: { marginRight: 8 } }}
              >
                官方網站
              </Button>
              <Popover
                withArrow
                styles={{
                  dropdown: {
                    width: 158,
                    height: 160,
                    fontSize: 12,
                    lineHeight: '14px',
                  },
                }}
              >
                <Popover.Target>
                  <Button
                    variant={'outline'}
                    leftSection={
                      <IconClock style={{ width: 16, height: 16 }} />
                    }
                    radius={'xl'}
                    style={{
                      width: 112,
                      height: 32,
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                    styles={{ section: { marginRight: 8 } }}
                  >
                    營業時間
                  </Button>
                </Popover.Target>
                <Popover.Dropdown>
                  <div>
                    <Stack gap={6}>
                      {Object.entries(dayMap).map(([key, { label }]) => {
                        const schedule =
                          service.operationHours?.weeklySchedule ?? [];
                        const day = schedule.find((w) => w.dayOfWeek === key);
                        const isUnknown = day?.isOpen === 'UNKNOWN';
                        const isOpen = day?.isOpen === 'OPEN';

                        const start =
                          day?.timeSlots?.[0]?.startTime
                            ?.split(':')
                            .slice(0, 2)
                            .join(':') ?? '--:--';

                        const end =
                          day?.timeSlots?.[0]?.endTime
                            ?.split(':')
                            .slice(0, 2)
                            .join(':') ?? '--:--';

                        return (
                          <Group key={key} gap={3}>
                            <div>{label}</div>

                            {isOpen ? (
                              <>
                                <div>{start}</div>
                                <div>-</div>
                                <div>{end}</div>
                              </>
                            ) : isUnknown ? (
                              <div style={{ color: 'grey' }}>未知</div>
                            ) : (
                              <div style={{ color: 'grey' }}>休息</div>
                            )}
                          </Group>
                        );
                      })}
                    </Stack>
                  </div>
                </Popover.Dropdown>
              </Popover>
              {/* 可用付款方式按鈕 */}
              <Popover
                withArrow
                styles={{
                  dropdown: {
                    fontSize: 12,
                    lineHeight: '14px',
                  },
                }}
              >
                <Popover.Target>
                  <Button
                    variant={'outline'}
                    leftSection={
                      <IconCreditCard style={{ width: 16, height: 16 }} />
                    }
                    disabled={service.paymentMethods.length === 0}
                    radius={'xl'}
                    style={{
                      width: 140,
                      height: 32,
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                    styles={{ section: { marginRight: 8 } }}
                  >
                    可用付款方式
                  </Button>
                </Popover.Target>
                <Popover.Dropdown>
                  <div>
                    <Stack gap={6} align={'start'}>
                      {paymentMethodNames.map((paymentMethod) => {
                        return (
                          <Text fz={12} fw={400}>
                            {paymentMethod}
                          </Text>
                        );
                      })}
                    </Stack>
                  </div>
                </Popover.Dropdown>
              </Popover>
            </Group>

            <Stack gap={10} style={{ minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  height: 92,
                }}
              >
                {/* 品牌名稱 */}
                <div
                  style={{
                    flex: 1,
                    backgroundColor:
                      colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                    height: '100%',
                    borderRadius: 12,
                  }}
                >
                  <Stack
                    gap={8}
                    style={{
                      paddingInline: 24,
                      paddingTop: 16,
                      paddingBottom: 16,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: '#868E96',
                      }}
                    >
                      品牌名稱
                    </Text>
                    <Text style={{ fontSize: 18, fontWeight: 400 }}>
                      {brand?.name ?? '-'}
                    </Text>
                  </Stack>
                </div>
                <div
                  style={{
                    flex: 1,
                    backgroundColor:
                      colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                    height: '100%',
                    borderRadius: 12,
                  }}
                >
                  <Stack
                    gap={8}
                    style={{
                      paddingInline: 24,
                      paddingTop: 16,
                      paddingBottom: 16,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: '#868E96',
                      }}
                    >
                      服務電話
                    </Text>
                    <Text style={{ fontSize: 18, fontWeight: 400 }}>
                      {service.phoneNumber
                        ? (() => {
                            try {
                              const parsed = phoneUtil.parse(
                                `${service.phoneNumber.countryCode}${service.phoneNumber.number}`,
                              );

                              return (
                                '(' +
                                service.phoneNumber.countryCode +
                                ') ' +
                                phoneUtil.format(
                                  parsed,
                                  PhoneNumberFormat.NATIONAL,
                                )
                              );
                            } catch {
                              return '-';
                            }
                          })()
                        : '-'}
                    </Text>
                  </Stack>
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  height: 92,
                  backgroundColor:
                    colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                  borderRadius: 12,
                }}
              >
                <Stack
                  gap={8}
                  style={{
                    paddingInline: 24,
                    paddingTop: 16,
                    paddingBottom: 16,
                  }}
                >
                  <Group gap={8}>
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: '#868E96',
                      }}
                    >
                      營運商店
                    </Text>
                  </Group>
                  {service.storeIds.length === 0 ? (
                    <Text
                      style={{
                        fontSize: 18,
                        fontWeight: 400,
                      }}
                    >
                      -
                    </Text>
                  ) : (
                    <Group gap={12}>
                      {service.storeIds.map((entry, index) => (
                        <React.Fragment key={`${entry.type}-${entry.value}`}>
                          {index > 0 && (
                            <Text style={{ fontSize: 18, fontWeight: 400 }}>
                              |
                            </Text>
                          )}
                          <Anchor
                            href={buildStoreUrl(entry)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 18, fontWeight: 400 }}
                          >
                            {'前往商店 - '}
                            {entry.type === 'PARKINGLOT'
                              ? '停車場'
                              : entry.type === 'GAS_STATION'
                                ? '加油站'
                                : '連鎖店'}
                          </Anchor>
                        </React.Fragment>
                      ))}
                    </Group>
                  )}
                </Stack>
              </div>
              <div
                style={{
                  display: 'flex',
                  height: 92,
                  minWidth: 0,
                  backgroundColor:
                    colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                  borderRadius: 12,
                  overflow: 'hidden',
                }}
              >
                <Stack
                  gap={8}
                  style={{
                    paddingInline: 24,
                    paddingTop: 16,
                    paddingBottom: 16,
                    flex: 1,
                    minWidth: 0,
                    maxWidth: 758,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 14,
                      fontWeight: 400,
                      color: '#868E96',
                    }}
                  >
                    備註文字
                  </Text>
                  <Tooltip
                    multiline
                    position="top-start"
                    label={
                      <Text style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>
                        {service.notes}
                      </Text>
                    }
                    disabled={!service.notes}
                    styles={{
                      tooltip: {
                        maxWidth: 300,
                      },
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 18,
                        fontWeight: 400,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                      }}
                    >
                      {service.notes
                        ? service.notes.split('\n').length > 1
                          ? `${service.notes.split('\n')[0]} ...`
                          : service.notes
                        : '-'}
                    </Text>
                  </Tooltip>
                </Stack>
              </div>
            </Stack>
          </div>

          {/* 右半邊 */}
          <div style={{ flex: 1 }}>
            <Group
              justify={'space-between'}
              style={{ height: 40, marginBottom: 22 }}
            >
              <Text style={{ fontSize: 20, fontWeight: 400 }}>充電資訊</Text>
              {evses.length && (
                <Button
                  variant={'filled'}
                  leftSection={<IconPlus />}
                  onClick={() => handleAddEVSEAndConnectorsClick(serviceId)}
                >
                  新增充電資訊
                </Button>
              )}
            </Group>

            {evses.length ? (
              <div style={{ height: 348, overflowY: 'scroll' }}>
                {evses.map((evse) => {
                  const numOfConnector = Object.values(evse.connectors).length;
                  return (
                    <div
                      style={{
                        border:
                          colorScheme === 'light'
                            ? '1px solid #DEE2E6'
                            : '1px solid #737373ff',
                        height: 198,
                        marginBottom: 20,
                        borderRadius: 16,
                        backgroundColor: '#12B886',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          backgroundColor:
                            colorScheme === 'light' ? 'white' : '#2E2E2E',
                          position: 'relative',
                          left: 15,
                          height: '100%',
                          marginRight: 15,
                          padding: 16,
                        }}
                      >
                        <Stack gap={16}>
                          <Group justify={'space-between'}>
                            <Group style={{ height: 24 }}>
                              <ThemeIcon color={'#12B886'} size={'24px'}>
                                <IconChargingPile size={'20px'} />
                              </ThemeIcon>
                              <Text>
                                {evse.evseSerialNumber
                                  ? evse.evseSerialNumber
                                  : '-'}
                                {' | '}
                                {evse.floorLevels
                                  ? evse.floorLevels.join(', ')
                                  : '-'}
                              </Text>
                            </Group>
                            <Group gap={8}>
                              <ActionIcon
                                variant={'light'}
                                size={'24px'}
                                color={'red'}
                              >
                                <IconTrash
                                  size={'20px'}
                                  onClick={() =>
                                    modals.openConfirmModal({
                                      title: (
                                        <Text
                                          style={{
                                            fontSize: 16,
                                            fontWeight: 700,
                                          }}
                                        >
                                          刪除充電資訊
                                        </Text>
                                      ),
                                      children: (
                                        <Text
                                          style={{
                                            fontSize: 14,
                                            fontWeight: 400,
                                          }}
                                        >
                                          是否確認刪除充電資訊？
                                        </Text>
                                      ),
                                      centered: true,
                                      labels: {
                                        confirm: '確認',
                                        cancel: '取消',
                                      },
                                      confirmProps: { color: 'blue' },

                                      onConfirm: async () => {
                                        handleDeleteEVSEAndConnectorsClick(
                                          serviceId,
                                          evse.id,
                                        );
                                      },
                                    })
                                  }
                                />
                              </ActionIcon>
                              <ActionIcon variant={'light'} size={'24px'}>
                                <IconEdit
                                  size={'20px'}
                                  onClick={() =>
                                    handleUpdateEVSEAndConnectorsClick(
                                      serviceId,
                                      evse.id,
                                    )
                                  }
                                />
                              </ActionIcon>
                            </Group>
                          </Group>

                          {/* 充電樁 & 充電槍 的資訊呈現 */}
                          {Object.values(evse.connectors).map(
                            (conn, connIndex) => (
                              <>
                                <div
                                  key={`${conn.id}-${connIndex}`}
                                  style={{
                                    backgroundColor:
                                      colorScheme === 'light'
                                        ? '#F8F9FA'
                                        : '#434343',
                                    height: 55,
                                    borderRadius: 4,
                                    paddingInline: 16,
                                    paddingTop: 7,
                                    paddingBottom: 8,
                                  }}
                                >
                                  <Group justify={'space-between'}>
                                    <Group gap={16}>
                                      <div
                                        style={{
                                          width: 6,
                                          height: 6,
                                          borderRadius: '50%',
                                          backgroundColor: '#12B886',
                                        }}
                                      />
                                      <Stack gap={4}>
                                        <Text
                                          style={{
                                            lineHeight: '20px',
                                            fontSize: 14,
                                            fontWeight: 400,
                                          }}
                                        >
                                          {conn.connectorSerialNumber !== ''
                                            ? conn.connectorSerialNumber
                                            : '-'}
                                        </Text>
                                        <Text
                                          style={{
                                            lineHeight: '16px',
                                            fontSize: 12,
                                            fontWeight: 400,
                                            color: '#868E96',
                                          }}
                                        >
                                          {getConnectorTypeLabel(
                                            conn.connectorType,
                                          )}{' '}
                                          · {conn.maxPower.value}
                                          {conn.maxPower.unit} ·{' '}
                                          {conn.powerType}
                                        </Text>
                                      </Stack>
                                    </Group>
                                    <Popover
                                      shadow={'xs'}
                                      position={'bottom'}
                                      withArrow
                                    >
                                      <Popover.Target>
                                        <Button
                                          variant={'transparent'}
                                          leftSection={
                                            <IconZoomMoney size={'16px'} />
                                          }
                                          style={{
                                            fontSize: 14,
                                            fontWeight: 400,
                                            color:
                                              conn.chargingRate.rateText
                                                .length !== 0
                                                ? '#228BE6'
                                                : '#868E96',
                                          }}
                                          styles={{
                                            root: { padding: 0 },
                                          }}
                                        />
                                      </Popover.Target>
                                      <Popover.Dropdown>
                                        <Text
                                          style={{
                                            fontSize: 12,
                                            fontWeight: 400,
                                            maxHeight: 56,
                                            maxWidth: 300,
                                            wordWrap: 'break-word',
                                            overflowY: 'auto',
                                          }}
                                        >
                                          {conn.chargingRate.rateText}
                                        </Text>
                                      </Popover.Dropdown>
                                    </Popover>
                                  </Group>
                                </div>
                              </>
                            ),
                          )}

                          {/* 缺少充電槍的地方會變成 新增 的按鈕 */}
                          {Array.from({
                            length: 2 - numOfConnector,
                          }).map((_, index) => (
                            <Button
                              key={`empty-${index}`}
                              variant={'filled'}
                              color={
                                colorScheme === 'light' ? '#F8F9FA' : '#434343'
                              }
                              leftSection={<IconPlus />}
                              onClick={() =>
                                handleAddChargingConnectorClick(
                                  serviceId,
                                  evse.id,
                                )
                              }
                              style={{
                                fontSize: 14,
                                fontWeight: 400,
                                width: '100%',
                                height: 55,
                                color: '#228BE6',
                              }}
                            >
                              新增充電槍
                            </Button>
                          ))}
                        </Stack>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div
                style={{
                  height: 348,
                  overflowY: 'scroll',
                }}
              >
                <Stack
                  style={{
                    height: 348,
                    position: 'relative',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: '#E9ECEF',
                    }}
                  >
                    <IconDatabaseOff
                      style={{
                        position: 'relative',
                        top: 8,
                        left: 8,
                        height: 24,
                        width: 24,
                        color: '#868E96',
                      }}
                    />
                  </div>
                  <Text
                    style={{
                      fontSize: 14,
                      fontWeight: 400,
                      color: '#868E96',
                    }}
                  >
                    目前還沒有任何充電資訊，點擊「新增充電資訊」開始建立！
                  </Text>
                  <Button
                    variant={'filled'}
                    leftSection={<IconPlus />}
                    onClick={() => handleAddEVSEAndConnectorsClick(serviceId)}
                  >
                    新增充電資訊
                  </Button>
                </Stack>
              </div>
            )}
          </div>

          {/* 中間分隔線 */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: '50%',
              width: 1,
              backgroundColor:
                colorScheme === 'light' ? '#DEE2E6' : '#737373ff',
              transform: 'translateX(-0.5px)',
            }}
          />
        </div>
      </Paper>
    </>
  );
};

export default ChargingServiceInformationCard;
